/**
 * Shared logic for finding the nearest stop on a route and computing the
 * insertion position for a to-be-assigned stop.
 *
 * Used by both ScheduleStopDialog (individual assign) and MapRoutingTab's
 * useWorkOrderEditState (configured-service details map routing) so that
 * both flows pre-position new stops consistently.
 */
import { get } from 'lodash'
import { getDistanceBetweenPoints } from 'utils/distance'

const isValidNumber = value => typeof value === 'number' && Number.isFinite(value)

const toFiniteNumber = value => {
  if (value === null || value === undefined || value === '') return null
  const num = parseFloat(value)
  return Number.isFinite(num) ? num : null
}

/**
 * Check whether a stop matches the given identifier across the common id fields.
 * Stops can carry workOrderId, id, and/or configuredServiceEventId — any match counts.
 */
const stopMatchesId = (stop, targetId) => {
  if (!stop || !targetId) return false
  return stop.workOrderId === targetId || stop.id === targetId || stop.configuredServiceEventId === targetId
}

/**
 * Finds the route stop with the smallest geographic distance to the given coordinates.
 * Returns null if no stop has valid coordinates or no input coords provided.
 *
 * @param {Object[]} stops - route stops with latitude/longitude fields
 * @param {number|string} lat
 * @param {number|string} lng
 * @returns {Object|null} the nearest stop or null
 */
export const findNearestStop = (stops, lat, lng) => {
  const targetLat = toFiniteNumber(lat)
  const targetLng = toFiniteNumber(lng)
  if (targetLat === null || targetLng === null || !Array.isArray(stops) || stops.length === 0) {
    return null
  }

  let minDistance = Infinity
  let bestStop = null

  stops.forEach(stop => {
    if (!stop) return
    // Use ?? not || so latitude: 0 is treated as a valid value
    const rawLat = stop.latitude ?? stop.lat
    const rawLng = stop.longitude ?? stop.lng
    const stopLat = toFiniteNumber(rawLat)
    const stopLng = toFiniteNumber(rawLng)
    if (stopLat === null || stopLng === null) return

    const distance = getDistanceBetweenPoints(targetLat, targetLng, stopLat, stopLng)
    if (distance < minDistance) {
      minDistance = distance
      bestStop = stop
    }
  })

  return bestStop
}

/**
 * Computes the 1-indexed sequence position to insert a to-be-assigned stop on a route.
 *
 * Resolution order:
 *  1. If an explicit targetStopId is provided AND found in routeStops → place AFTER it
 *  2. Otherwise, find the geographically nearest stop and place AFTER it
 *  3. Fallback to the end of the route
 *
 * @param {Object} params
 * @param {Object[]} params.routeStops - existing stops on the destination route
 * @param {string} [params.targetStopId] - explicit target (e.g., user picked from dropdown)
 * @param {number|string} [params.lat] - to-be-assigned stop's latitude
 * @param {number|string} [params.lng] - to-be-assigned stop's longitude
 * @returns {{ sequence: number, nearestStop: Object|null }}
 */
export const computeStopInsertionPosition = ({ routeStops, targetStopId, lat, lng }) => {
  if (!Array.isArray(routeStops)) {
    return { sequence: 1, nearestStop: null }
  }

  // Explicit target selection — place right after it
  if (targetStopId) {
    const targetIndex = routeStops.findIndex(s => stopMatchesId(s, targetStopId))
    if (targetIndex !== -1) {
      return { sequence: targetIndex + 2, nearestStop: routeStops[targetIndex] }
    }
  }

  // Distance-based: pick the geographically nearest stop on this route
  const nearest = findNearestStop(routeStops, lat, lng)
  if (nearest) {
    const bestIndex = routeStops.indexOf(nearest)
    if (bestIndex !== -1) {
      return { sequence: bestIndex + 2, nearestStop: nearest }
    }
  }

  // Fallback: end of route
  return { sequence: routeStops.length + 1, nearestStop: null }
}

export { isValidNumber }
